import React, { useCallback } from 'react';
import update from 'immutability-helper';
import { useDrag, useDrop } from 'react-dnd';

import {
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Quiz as QuizIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { TrainingQuestion } from '../../types/training';

const dragStyle = {
  border: '1px dashed #0072CE',
  padding: '0.5rem 1rem',
  marginBottom: '0.5rem',
  backgroundColor: 'white',
  cursor: 'move',
  borderRadius: '8px',
  transition: 'all 0.2s ease-in-out',
};

const MyCard = React.forwardRef<
  HTMLDivElement,
  {
    text: string;
    index: number;
    moveCard: (dragIndex: number, hoverIndex: number) => void;
    answerText: string;
    onRemoveQuestion: (index: number) => void;
    questionNumber: number;
  }
>(
  (
    { text, index, moveCard, answerText, onRemoveQuestion, questionNumber },
    ref
  ) => {
    const [, drop] = useDrop({
      accept: 'card',
      hover(item: { index: number }, monitor) {
        if (!ref || typeof ref === 'function' || !ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = ref.current.getBoundingClientRect();
        // Get vertical middle
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        // Determine mouse position
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;

        // Get pixels to the top
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        // Time to actually perform the action
        moveCard(dragIndex, hoverIndex);
        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: 'card',
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const opacity = isDragging ? 0.5 : 1;

    // Combine refs for drag and drop
    const combinedRef = (node: HTMLDivElement) => {
      if (ref && typeof ref === 'object') {
        ref.current = node;
      }
      drag(drop(node));
    };

    return (
      <Card
        ref={combinedRef}
        style={{ ...dragStyle, opacity }}
        sx={{
          '&:hover': {
            boxShadow: 3,
            transform: 'translateY(-1px)',
          },
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={1}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', height: '100%' }}
              >
                <DragIcon color="action" />
              </Box>
            </Grid>

            <Grid item xs={1}>
              <Chip
                label={`Q${questionNumber}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Grid>

            <Grid item xs={8}>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {text}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary">
                  Correct Answer: {answerText}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={2} sx={{ textAlign: 'right' }}>
              <IconButton
                color="error"
                onClick={() => onRemoveQuestion(index)}
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'white',
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }
);

MyCard.displayName = 'MyCard';

interface QuestionListProps {
  questions: TrainingQuestion[];
  onRemoveQuestion: (index: number) => void;
  onQuestionOrderUpdate: (questions: TrainingQuestion[]) => void;
}

const QuestionList: React.FC<QuestionListProps> = ({
  questions,
  onQuestionOrderUpdate,
  onRemoveQuestion,
}) => {
  const moveCard = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const dragCard = questions[dragIndex];
      onQuestionOrderUpdate(
        update(questions, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragCard],
          ],
        })
      );
    },
    [questions, onQuestionOrderUpdate]
  );

  const getAnswerText = (question: TrainingQuestion): string => {
    const answerMap: { [key: string]: string } = {
      a: question.selection_a,
      b: question.selection_b,
      c: question.selection_c,
      d: question.selection_d,
      e: question.selection_e,
    };

    return answerMap[question.answer] || question.answer;
  };

  const renderCard = (card: TrainingQuestion, index: number) => {
    return (
      <Grid item xs={12} key={`${card.order}-${index}`}>
        <MyCard
          index={index}
          text={card.question}
          answerText={getAnswerText(card)}
          onRemoveQuestion={onRemoveQuestion}
          moveCard={moveCard}
          questionNumber={index + 1}
        />
      </Grid>
    );
  };

  if (questions.length === 0) {
    return (
      <Card sx={{ p: 3, textAlign: 'center' }}>
        <QuizIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No questions added yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Add questions to create a quiz for this training
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <QuizIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h6" color="primary">
          Quiz Questions ({questions.length})
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Drag and drop to reorder questions. Click the delete icon to remove a
        question.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={1}>
        {questions.map((card, i) => renderCard(card, i))}
      </Grid>
    </Box>
  );
};

export default QuestionList;
